import * as feedActions from '../actions/feed.actions';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AnyFilterItem, findFilter } from '../reducers/feed.reducer';
import { FilterItem } from '../models/core.models';
import { EMPTY, Observable, of } from 'rxjs';
import { FeedService } from '../services/feed.service';
import { FilterItemResolveResponse } from 'src/app/api/queries/feed-api.service';
import { NomObjectTypes } from '../constants';
import {
  applyDefaultPeriod,
  periodChanged,
  selectPreviousPeriod,
  toggleDarkModeSuccess,
  togglePeriodLockedSuccess,
} from '../actions/core.actions';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  skip,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  getContentFilters,
  selectCurrentObject,
  selectFeed,
  selectFeedFilters,
  selectFeedSettings,
  selectUser,
} from '../reducers';
import {
  saveObjectExportSettingsState,
  saveObjectOutputSettingsStateFail,
  saveObjectOutputSettingsStateSuccess,
} from '../actions/object.actions';
import { Injectable, inject } from '@angular/core';
import { Location } from '@angular/common';
import { OutputSettingsService } from '../services/output-settings.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { concatLatestFrom } from '@ngrx/operators';
import isEqual from 'lodash-es/isEqual';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { allWidgetDataQueriesFilter } from 'src/app/api/queries/widget.queries';
import { FeedApiService } from 'src/app/api/queries/feed-api.service';
import { feedPostsQueryKey } from 'src/app/api/queries/feed.queries';
import { FeedPostsService } from '../services/feed-posts.service';
import { FeedFiltersService } from '../services/feed-filters.service';
import { DialogService } from '@sui/dialog';
import { BusinessConstraintsInfoDialogComponent } from '../components/popups/business-constraints-info-dialog/business-constraints-info-dialog.component';

@Injectable()
export class FeedEffects {
  private readonly queryClient = inject(QueryClient);
  private readonly feedApi = inject(FeedApiService);
  private readonly feedPosts = inject(FeedPostsService);
  private readonly feedFilters = inject(FeedFiltersService);

  /**
   * Anything that changes what the feed shows: refresh the widgets, re-read
   * every expanded filter, and reload the posts list when the cue did not
   * already do so by moving its query key.
   */
  filtersChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          periodChanged,
          applyDefaultPeriod,
          selectPreviousPeriod,
          feedActions.activateFilters,
          feedActions.clearFilters,
          feedActions.deactivateFilterItem,
          feedActions.updateActiveFilterItem,
          feedActions.feedAttachDimensionsSuccess,
          feedActions.feedDetachDimensionSuccess,
          feedActions.editFeedSuccess,
          feedActions.searchPosts,
          feedActions.removePostFromFeedSuccess,
          feedActions.removeSelectedPostsFromFeedSuccess,
          feedActions.setDimensionJoinTypeSuccess,
          // ! These three are dispatched *after* their write resolves, not as its
          // ! trigger - dispatching them up front races the reload against it.
          feedActions.deleteRemovedItemSuccess,
          feedActions.removeAllItemsBySource,
          feedActions.clearAllRemovedItems,
          feedActions.createAndAttachDimensionSuccess,
          feedActions.showSelectedPostsOnly,
        ),
        withLatestFrom(
          this.store.select(selectCurrentObject),
          this.store.select(selectFeed),
          this.store.select(selectFeedFilters),
          this.store.select(getContentFilters),
        ),
        switchMap(([action, object, feed, filters, contentFilters]) => {
          // ! Posts: a cue that changes `getContentFilters` moves the list
          // ! query's key, which refetches on its own. But some cues leave the
          // ! key unchanged - `activateFilters` re-dispatched as a refresh
          // ! signal (breadcrumb chip / Refresh item, dashboard auto-refresh,
          // ! drilldown on an already-active value) - and those can only reload
          // ! via invalidation. The post-cue key already sitting in the cache
          // ! is the tell: an unchanged key is always cached, a new key fetches
          // ! by itself. (Write cues stay in `reloadFeedPostsOnFeedWrite$`.)
          if (feed) {
            const postsKey = feedPostsQueryKey({
              feedId: feed.id,
              contentFilters,
            });

            if (this.queryClient.getQueryState(postsKey)) {
              this.feedPosts.invalidate();
            }
          }

          // ! No edit-mode guard on purpose. These actions are also the
          // ! Dashboard's "reload the widgets" signal (auto-refresh, the toolbar
          // ! Refresh button, the breadcrumb chips), and gating them on the feed's
          // ! compact-view flag silently killed all of it on dashboards. Compact
          // ! view needs no guard anyway: it renders `widget-card-list`, so no
          // ! widget-data query is mounted for this to invalidate.
          this.queryClient.invalidateQueries(allWidgetDataQueriesFilter());

          // ! Every already-expanded filter is re-read from offset 0: the item
          // ! counts are narrowed by the content filters that just changed. A
          // ! filter still at `offset === -1` was never expanded.
          filters.forEach((filter) => {
            if (filter.offset !== -1) {
              this.feedFilters.loadFilterItems(
                object.id,
                filter,
                contentFilters,
                0,
              );
            }
          });

          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  $widgetsViewHasChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.changeWidgetsLanaguage, toggleDarkModeSuccess),
        withLatestFrom(this.store.select(selectUser)),
        switchMap(([action, user]) => {
          if (!user) {
            return EMPTY; // do nothing if no user as dark mode can be changed on login screen
          }

          // * Language is part of the query key, but dark mode is not: the
          // * backend renders the chart with the user's `dark_mode` preference,
          // * so the response changes without any request param changing.
          this.queryClient.invalidateQueries(allWidgetDataQueriesFilter());

          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  /**
   * The other half of `filtersChanged$`'s cue list: writes that change **what
   * the server would answer** without changing a single request parameter, so
   * the key is identical and only an invalidation reloads the list.
   *
   * The list is not blanked first - rows stay on screen while the refetch is in
   * flight.
   */
  reloadFeedPostsOnFeedWrite$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          feedActions.feedAttachDimensionsSuccess,
          feedActions.feedDetachDimensionSuccess,
          feedActions.createAndAttachDimensionSuccess,
          feedActions.setDimensionJoinTypeSuccess,
          feedActions.editFeedSuccess,
          feedActions.removePostFromFeedSuccess,
          feedActions.removeSelectedPostsFromFeedSuccess,
          feedActions.deleteRemovedItemSuccess,
          feedActions.removeAllItemsBySource,
          feedActions.clearAllRemovedItems,
        ),
        tap(() => this.feedPosts.invalidate()),
      ),
    { dispatch: false },
  );

  feedCreateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.createFeedSuccess),
        tap((action) => {
          const messageKey =
            action.feed.object_type_id != NomObjectTypes.SEARCH_DIMENSION
              ? 'feed.toasts.feed-created-successfully'
              : 'feed.toasts.dimension-created-successfully';
          this.toastService.showSuccess(
            this.translateService.instant(messageKey),
          );

          // Navigate to the newly created feed
          if (action.feed.object_type_id != NomObjectTypes.SEARCH_DIMENSION) {
            // navigate to path: 'workspace/:workspaceId/feed/:feedId',
            this.router.navigate([
              'analytics',
              'workspace',
              action.feed.workspace_id,
              'feed',
              action.feed.id,
            ]);
          } else {
            // navigate to path: 'workspace/:workspaceId/clipping-feed/:feedId',
            this.router.navigate([
              'analytics',
              'workspace',
              action.feed.workspace_id,
            ]);
          }
        }),
      ),
    { dispatch: false },
  );

  periodChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(periodChanged),
        tap((action) => {
          this.updateQueryParamsSilently({
            start_time: action.period.startTimestamp,
            end_time: action.period.endTimestamp,
          });
        }),
      ),
    { dispatch: false },
  );

  defaultPeriodChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(applyDefaultPeriod),
        tap((action) => {
          this.updateQueryParamsSilently({
            start_time: null,
            end_time: null,
          });
        }),
      ),
    { dispatch: false },
  );

  togglePeriodLockedSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(togglePeriodLockedSuccess),
        tap(({ isPeriodLocked }) => {
          if (isPeriodLocked) {
            this.updateQueryParamsSilently({
              start_time: null,
              end_time: null,
            });
          }
        }),
      ),
    { dispatch: false },
  );

  pageChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.pageChanged),
        tap((action) => {
          this.updateQueryParamsSilently({
            page: action.pageNumber,
          });
        }),
      ),
    { dispatch: false },
  );

  duplicateFeedsConstraintError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.duplicateFeedsConstraintError),
        tap((action) => {
          this.dialogService.openSimpleDialog(
            BusinessConstraintsInfoDialogComponent,
            {
              data: {
                errors: action.errors,
              },
              closeOnClick: false,
            },
          );
        }),
      ),
    { dispatch: false },
  );

  /**
   * Updates the query parameters silently by creating a new URL tree with the provided parameters
   * and updating the URL without adding a history entry.
   *
   * @param params - The query parameters to be updated.
   */
  updateQueryParamsSilently(params: { [key: string]: any }) {
    // First, get the current router state
    const currentRoute = this.router.routerState.snapshot.root;

    // Then, extract the current query parameters
    let currentQueryParams = {};
    if (currentRoute) {
      currentQueryParams = currentRoute.queryParams;
    }

    // Merge the current query parameters with the new ones
    const mergedQueryParams = { ...currentQueryParams, ...params };

    // Now, create a URL tree with the merged query parameters
    const url = this.router
      .createUrlTree([], { queryParams: mergedQueryParams })
      .toString();

    // Use replaceState to update the URL without adding a history entry
    this.location.replaceState(url);
  }

  toggleFilterDimension$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.toggleFilterDimension),
      filter((action) => action.activate === true),
      map(() => feedActions.activateFilters()),
    ),
  );

  selectFilterItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.selectFilterItem),
      concatLatestFrom(() => this.store.select(selectFeedFilters)),
      switchMap(([{ filterItem, mode }, filters]) => {
        const existingFilter = findFilter(filters, filterItem);

        // Case 1: found in loaded filters – just reuse its meta
        if (existingFilter) {
          const resolvedItem: AnyFilterItem = {
            ...filterItem,
            type: mode,
            filter_title: existingFilter.name,
          };

          return of(
            feedActions.selectFilterItemResolved({ item: resolvedItem }),
          );
        }

        // Case 2: not found – call backend
        //
        // ! An effect on purpose, not a hook: nothing holds the response, it only
        // ! enriches one action with titles, and most of its dispatch sites are
        // ! `widget.effects`' point-click cluster, which builds `Action[]` arrays.
        const request$: Observable<FilterItemResolveResponse> =
          this.feedApi.getValueFilterItemMeta(
            filterItem.property_uri,
            (filterItem as FilterItem).raw_value,
          );

        return request$.pipe(
          switchMap((response) => {
            const resolvedItem: AnyFilterItem = {
              ...filterItem,
              type: mode,
              title: response.property_value_title ?? filterItem.title,
              filter_title: response.property_title ?? filterItem.filter_title,
            };

            return of(
              feedActions.selectFilterItemResolved({ item: resolvedItem }),
            );
          }),
          catchError((error) =>
            of(feedActions.selectFilterItemResolveFailed({ error })),
          ),
        );
      }),
    ),
  );

  saveOutputSettingsState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveObjectExportSettingsState),
      withLatestFrom(this.store.select(selectFeed)),
      switchMap(([action, feed]) =>
        this.outputSettings
          .saveObjectExportSettingsState(
            action.contextObject,
            action.settingsType,
            action.settings,
          )
          .pipe(
            map(() =>
              saveObjectOutputSettingsStateSuccess({
                objectId: action.contextObject.id,
                objectClassId: action.contextObject.object_class_id,
                settingsType: action.settingsType,
                settings: action.settings,
              }),
            ),
            catchError((error) =>
              of(saveObjectOutputSettingsStateFail({ error })),
            ),
          ),
      ),
    ),
  );

  saveFeedSettings$ = createEffect(() =>
    this.store.select(selectFeed).pipe(
      // Ensure we actually have a feed before listening to settings
      filter((feed): feed is NonNullable<typeof feed> => !!feed?.id),
      distinctUntilChanged((a, b) => isEqual(a.id, b.id)),
      // Switch to the settings stream for THIS specific feed
      switchMap((feed) =>
        this.store.select(selectFeedSettings).pipe(
          // Skip the very first emission that happens when the feed loads
          skip(1),
          distinctUntilChanged((a, b) => isEqual(a, b)),
          switchMap((feedSettings) =>
            this.feedService.saveFeedSettings(feed.id, feedSettings).pipe(
              map((response) =>
                feedActions.saveFeedSettingsSuccess({ response }),
              ),
              catchError((error) =>
                of(feedActions.saveFeedSettingsFail({ error })),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  // ! The removed-items quartet — `fetchRemovedItems$`, `deleteRemovedItem$`,
  // ! `deleteAllRemovedItem$` and `removeAllItemsBySource$` — is gone (step 14c).
  // ! `injectFeedRemovedItemsQuery` holds the list and the four mutation hooks
  // ! patch it; see the removed-items block in `feed.queries.ts`.

  constructor(
    private actions$: Actions,
    private feedService: FeedService,
    private store: Store,
    private toastService: ToastService,
    private router: Router,
    private location: Location,
    private outputSettings: OutputSettingsService,
    private translateService: TranslateService,
    private dialogService: DialogService,
  ) {}
}
